import { useEffect, useMemo } from 'react';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Image,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { modals } from '@mantine/modals';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { intervalToDuration } from 'date-fns';

import { useSessionCreationContext } from '../../SessionCreationContext';
import {
  type ScenarioConfigFormValues,
  type ScenarioBodyImageConfig,
  type ScenarioExposureConfig,
  type ScenarioPerformanceConfig,
} from './formSchema';
import { getAppConfig } from '../../../../../configs/apps';
import { exposureItemOptionsGrouped } from '../../../../../data/foods';
import {
  getExposureSpawnPositions,
  scenarioMaps,
} from '../../../../../data/maps';
import { getProductIconUrl } from '../../../../../types/contants';

/** Options for multiItem slot Select: empty ('') + grouped catalog. */
const multiItemSlotOptions = [
  { value: '', label: '—' },
  ...exposureItemOptionsGrouped,
];

type ScenarioFormProps = {
  onSubmit?: (values: ScenarioConfigFormValues) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /** Se false, il tab Performance è disabilitato e mostra (Non attivo nel piano) */
  isPerformanceActive?: boolean;
  /** Se false, il tab Exposure è disabilitato e mostra (Non attivo nel piano) */
  isExposureActive?: boolean;
  /** Se false, il tab Body Image è disabilitato e mostra (Non attivo nel piano) */
  isBodyImageActive?: boolean;
};

const getPerformanceDefaults = (): ScenarioPerformanceConfig => {
  const config = getAppConfig('performance');
  return {
    map: scenarioMaps.performance?.[0]?.value ?? '',
    currentState: config.defaultLevel ?? '',
    variation: config.defaultVariation ?? 0,
    duration: config.defaultDuration ?? 300,
  };
};

const getExposureDefaults = (): ScenarioExposureConfig => {
  return {
    map: scenarioMaps.exposure?.[0]?.value ?? '',
    itemIds: [],
    multiItem: false,
    hasTutorial: false,
    userSpawnPosition: '1',
  };
};

const getBodyImageDefaults = (): ScenarioBodyImageConfig => ({});

export const createEmptyScenarioConfig = (): ScenarioConfigFormValues => ({
  scenarioType: 'performance',
  performance: getPerformanceDefaults(),
  // Non includere exposure quando scenarioType è 'performance'
  // per evitare errori di validazione su campi che non sono usati
});

export const EMPTY_SCENARIO_CONFIG = createEmptyScenarioConfig();

export function ScenarioForm({
  onSubmit,
  onDirtyChange,
  isPerformanceActive = true,
  isExposureActive = true,
  isBodyImageActive = true,
}: ScenarioFormProps) {
  const form = useFormContext<ScenarioConfigFormValues>();

  if (!form) {
    throw new Error('ScenarioForm must be used within a FormProvider');
  }

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    formState: { isDirty, isValid },
  } = form;

  const values = useWatch({ control });
  const scenarioType = useWatch({
    control,
    name: 'scenarioType',
  });

  const {
    setStepValue,
    setStepValidity,
    registerStepValidator,
    unregisterStepValidator,
  } = useSessionCreationContext();

  useEffect(() => {
    console.log('[ScenarioForm] Form state changed:', {
      isValid,
      isDirty,
      values,
      errors: form.formState.errors,
    });
    setStepValue('configuration', values);
    setStepValidity('configuration', isValid);
    onDirtyChange?.(isDirty);
  }, [
    values,
    isDirty,
    isValid,
    setStepValue,
    setStepValidity,
    onDirtyChange,
    form.formState.errors,
  ]);

  useEffect(() => {
    const validator = async () => {
      const valid = await trigger();
      setStepValidity('configuration', valid);
      if (valid) {
        setStepValue('configuration', getValues());
      }
      return valid;
    };

    registerStepValidator('configuration', validator);

    return () => {
      unregisterStepValidator('configuration');
      setStepValidity('configuration', false);
      setStepValue('configuration', null);
    };
  }, [
    registerStepValidator,
    unregisterStepValidator,
    trigger,
    getValues,
    setStepValidity,
    setStepValue,
  ]);

  useEffect(() => {
    if (scenarioType === 'performance') {
      const performance = getValues('performance');
      console.log('[ScenarioForm] Performance check:', {
        scenarioType,
        performance,
        hasCurrentState: Boolean(performance?.currentState),
        hasMap: Boolean(performance?.map),
      });
      if (!performance || !performance.currentState || !performance.map) {
        const defaults = getPerformanceDefaults();
        console.log('[ScenarioForm] Setting performance defaults:', defaults);
        setValue('performance', defaults, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
        void trigger('performance');
      }
    }
    if (scenarioType === 'exposure') {
      const exposure = getValues('exposure');
      if (!exposure || !exposure.map || exposure.itemIds === undefined) {
        setValue('exposure', getExposureDefaults(), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
        void trigger('exposure');
      }
    }
    if (scenarioType === 'body_image') {
      const bodyImage = getValues('bodyImage');
      if (!bodyImage) {
        setValue('bodyImage', getBodyImageDefaults(), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
        void trigger('bodyImage');
      }
    }
  }, [scenarioType, getValues, setValue, trigger]);

  // Auto-switch to an active tab if the current one is disabled
  useEffect(() => {
    const currentActive =
      scenarioType === 'performance'
        ? isPerformanceActive
        : scenarioType === 'exposure'
          ? isExposureActive
          : isBodyImageActive;
    if (currentActive) {
      return;
    }
    const fallback = isPerformanceActive
      ? 'performance'
      : isExposureActive
        ? 'exposure'
        : isBodyImageActive
          ? 'body_image'
          : null;
    if (fallback && scenarioType !== fallback) {
      setValue('scenarioType', fallback, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
      void trigger();
    }
  }, [
    scenarioType,
    isPerformanceActive,
    isExposureActive,
    isBodyImageActive,
    setValue,
    trigger,
  ]);

  const { t } = useTranslation();
  const scenarioTypeOptions = useMemo(() => {
    const notActiveSuffix = t(
      'scenario_tab_not_active',
      '(Non attivo nel piano)'
    );
    return [
      {
        label: isPerformanceActive
          ? t('scenario_type_performance', 'Performance')
          : `${t('scenario_type_performance', 'Performance')} ${notActiveSuffix}`,
        value: 'performance',
        icon: (
          <Image
            src={getProductIconUrl('performance')}
            alt='Augmented Performance'
            w={20}
            h={20}
            fit='contain'
          />
        ),
        disabled: !isPerformanceActive,
      },
      {
        label: isExposureActive
          ? t('scenario_type_exposure', 'Exposure')
          : `${t('scenario_type_exposure', 'Exposure')} ${notActiveSuffix}`,
        value: 'exposure',
        icon: (
          <Image
            src={getProductIconUrl('exposure')}
            alt='Augmented Exposure'
            w={20}
            h={20}
            fit='contain'
          />
        ),
        disabled: !isExposureActive,
      },
      {
        label: isBodyImageActive
          ? t('scenario_type_body_image', 'Body Image')
          : `${t('scenario_type_body_image', 'Body Image')} ${notActiveSuffix}`,
        value: 'body_image',
        icon: (
          <Image
            src={getProductIconUrl('body_image')}
            alt='Body Image'
            w={20}
            h={20}
            fit='contain'
          />
        ),
        disabled: !isBodyImageActive,
      },
    ];
  }, [t, isPerformanceActive, isExposureActive, isBodyImageActive]);

  return (
    <form
      onSubmit={handleSubmit(data => {
        onSubmit?.(data);
      })}
      className='flex flex-col gap-4'
    >
      <Stack gap='sm'>
        <Text fw={600} size='sm'>
          {t('scenario_mode_title', 'Seleziona la modalità di scenario')}
        </Text>
        <Controller
          control={control}
          name='scenarioType'
          render={({ field }) => (
            <Tabs
              value={field.value}
              onChange={value => {
                if (!value) {
                  return;
                }
                field.onChange(
                  value as ScenarioConfigFormValues['scenarioType']
                );
              }}
            >
              <Tabs.List>
                {scenarioTypeOptions.map(option => (
                  <Tabs.Tab
                    value={option.value}
                    key={option.value}
                    disabled={option.disabled}
                    leftSection={option.icon}
                  >
                    {option.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs>
          )}
        />
        <Card withBorder mt='sm' p='md' bg='gray.0'>
          {scenarioType === 'performance' ? (
            <PerformanceScenarioFields />
          ) : scenarioType === 'body_image' ? (
            <BodyImageScenarioFields />
          ) : (
            <ExposureScenarioFields />
          )}
        </Card>
      </Stack>
    </form>
  );
}

function BodyImageScenarioFields() {
  const { t } = useTranslation();

  return (
    <Stack gap='sm'>
      <Text fw={500}>
        {t('scenario_body_image_title', 'Impostazioni Body Image')}
      </Text>
      <Text size='sm' c='dimmed'>
        {t(
          'scenario_body_image_description',
          "Questa sessione non richiede una configurazione iniziale aggiuntiva. Le misure più recenti dell'utente verranno confermate prima dell’avvio."
        )}
      </Text>
    </Stack>
  );
}

function PerformanceScenarioFields() {
  const { control } = useFormContext<ScenarioConfigFormValues>();
  const { t } = useTranslation();
  const appConfig = useMemo(() => getAppConfig('performance'), []);
  const stateOptions = useMemo(() => appConfig.stateOptions ?? [], [appConfig]);
  const mapOptions = useMemo(() => scenarioMaps.performance ?? [], []);
  const durationSeconds = useWatch({
    name: 'performance.duration',
  });
  const durationLabel = useMemo(() => {
    if (!durationSeconds || durationSeconds <= 0) {
      return t('pp_duration_zero', '0 minuti');
    }

    const breakdown = intervalToDuration({
      start: 0,
      end: durationSeconds * 1000,
    });

    const hours = breakdown.hours ?? 0;
    const minutes = breakdown.minutes ?? 0;

    if (hours >= 1) {
      if (minutes > 0) {
        return t('pp_duration_hours_minutes', '{{hours}}h {{minutes}}m', {
          hours,
          minutes,
        });
      }
      return t('pp_duration_hours', '{{hours}}h', { hours });
    }

    const roundedMinutes = Math.max(1, Math.round(durationSeconds / 60));
    return t('pp_duration_minutes_label', '{{minutes}} minuti', {
      minutes: roundedMinutes,
    });
  }, [durationSeconds, t]);

  return (
    <Stack gap='sm'>
      <Text fw={500}>
        {t('scenario_performance_title', 'Impostazioni Performance')}
      </Text>
      <Controller
        control={control}
        name='performance.map'
        render={({ field, fieldState }) => (
          <Select
            label={t('scenario_map_label', 'Scenario')}
            placeholder={t('scenario_map_placeholder', 'Seleziona lo scenario')}
            data={mapOptions}
            value={field.value ?? null}
            onChange={value => field.onChange(value ?? '')}
            error={fieldState.error?.message}
          />
        )}
      />
      {appConfig.hasLevel && (
        <Controller
          control={control}
          name='performance.currentState'
          render={({ field, fieldState }) => (
            <Select
              label={t('pp_level', 'Livello')}
              placeholder={t(
                'pp_level_placeholder',
                'Seleziona il livello di partenza'
              )}
              data={stateOptions}
              value={field.value ?? null}
              onChange={value => field.onChange(value ?? '')}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {appConfig.hasVariation && (
        <Controller
          control={control}
          name='performance.variation'
          render={({ field, fieldState }) => (
            <NumberInput
              label={t('pp_variation', 'Variazione in percentuale')}
              rightSection='%'
              value={field.value ?? appConfig.defaultVariation ?? 0}
              onChange={value => {
                const nextValue =
                  typeof value === 'number' ? value : Number(value);
                field.onChange(Number.isNaN(nextValue) ? undefined : nextValue);
              }}
              min={0}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {appConfig.hasDuration && (
        <Controller
          control={control}
          name='performance.duration'
          render={({ field, fieldState }) => (
            <Stack gap={4}>
              <Group justify='space-between'>
                <Text size='sm' fw={500}>
                  {t('pp_duration', 'Durata massima sessione (in secondi)')}
                </Text>
                <Text size='xs' c='dimmed'>
                  {durationLabel}
                </Text>
              </Group>
              <NumberInput
                value={field.value ?? appConfig.defaultDuration ?? 0}
                onChange={value => {
                  const nextValue =
                    typeof value === 'number' ? value : Number(value);
                  field.onChange(
                    Number.isNaN(nextValue) ? undefined : nextValue
                  );
                }}
                min={1}
                rightSection='s'
                error={fieldState.error?.message}
              />
            </Stack>
          )}
        />
      )}
    </Stack>
  );
}

const GROUP_SIZE = 4;

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function ExposureScenarioFields() {
  const { control, setValue } = useFormContext<ScenarioConfigFormValues>();
  const { t } = useTranslation();
  const mapOptions = useMemo(() => scenarioMaps.exposure ?? [], []);
  const selectedMap = useWatch({
    control,
    name: 'exposure.map',
  });
  const userSpawnPosition = useWatch({
    control,
    name: 'exposure.userSpawnPosition',
  });
  const multiItem = useWatch({
    control,
    name: 'exposure.multiItem',
    defaultValue: false,
  });
  const itemIds = useWatch({
    control,
    name: 'exposure.itemIds',
    defaultValue: [],
  });

  const spawnOptions = useMemo(
    () => getExposureSpawnPositions(selectedMap),
    [selectedMap]
  );

  useEffect(() => {
    if (spawnOptions.length === 0) {
      if (userSpawnPosition && userSpawnPosition !== '1') {
        setValue('exposure.userSpawnPosition', '1', { shouldDirty: true });
      }
      return;
    }
    const stillValid = spawnOptions.some(
      opt => opt.value === userSpawnPosition
    );
    if (!stillValid) {
      setValue('exposure.userSpawnPosition', spawnOptions[0].value, {
        shouldDirty: true,
      });
    }
  }, [spawnOptions, userSpawnPosition, setValue]);

  const hasSelection =
    Array.isArray(itemIds) &&
    itemIds.length > 0 &&
    itemIds.some((id: number) => id != null && Number.isFinite(id) && id !== 0);

  const handleMultiItemChange = (newMultiItem: boolean) => {
    const apply = () => {
      setValue('exposure.itemIds', [], { shouldDirty: true });
      setValue('exposure.multiItem', newMultiItem, { shouldDirty: true });
    };
    if (hasSelection) {
      modals.openConfirmModal({
        title: t(
          'fe_multi_item_switch_confirm_title',
          'Cambio modalità multipla'
        ),
        children: (
          <Text size='sm'>
            {t(
              'fe_multi_item_switch_confirm_message',
              'Cambiando modalità la selezione elementi attuale verrà cancellata. Continuare?'
            )}
          </Text>
        ),
        labels: {
          confirm: t('confirm', 'Conferma'),
          cancel: t('cancel', 'Annulla'),
        },
        onConfirm: apply,
      });
    } else {
      apply();
    }
  };

  return (
    <Stack gap='sm'>
      <Text fw={500}>
        {t('scenario_exposure_title', 'Impostazioni Exposure')}
      </Text>
      <Controller
        control={control}
        name='exposure.map'
        render={({ field, fieldState }) => (
          <Select
            label={t('scenario_map_label', 'Scenario')}
            placeholder={t('scenario_map_placeholder', 'Seleziona lo scenario')}
            data={mapOptions}
            value={field.value ?? null}
            onChange={value => field.onChange(value ?? '')}
            error={fieldState.error?.message}
          />
        )}
      />
      {spawnOptions.length > 0 && (
        <Controller
          control={control}
          name='exposure.userSpawnPosition'
          render={({ field, fieldState }) => (
            <Select
              label={t('fe_user_spawn_position', 'Posizione di spawn')}
              placeholder={t(
                'fe_user_spawn_position_placeholder',
                'Seleziona la posizione'
              )}
              data={spawnOptions}
              value={field.value ?? null}
              onChange={value =>
                field.onChange(value ?? spawnOptions[0]?.value ?? '1')
              }
              error={fieldState.error?.message}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name='exposure.multiItem'
        render={({ field }) => (
          <Switch
            checked={Boolean(field.value)}
            onChange={event => handleMultiItemChange(event.currentTarget.checked)}
            label={t(
              'fe_multi_item_mode_title',
              'Modalità multipla'
            )}
            description={t(
              'fe_multi_item_mode_description',
              'Mostra più elementi contemporaneamente (gruppi fino a 4)'
            )}
          />
        )}
      />
      <Controller
        control={control}
        name='exposure.hasTutorial'
        render={({ field }) => (
          <Switch
            checked={Boolean(field.value)}
            onChange={event => field.onChange(event.currentTarget.checked)}
            label={t('fe_has_tutorial', 'Includi il tutorial')}
            description={t(
              'fe_has_tutorial_description',
              'Mostra il tutorial all’inizio della sessione'
            )}
          />
        )}
      />
      {!multiItem && (
        <Controller
          control={control}
          name='exposure.itemIds'
          render={({ field, fieldState }) => (
            <MultiSelect
              label={t('fe_foods', 'Lista elementi')}
              placeholder={t(
                'fe_foods_placeholder',
                'Seleziona gli elementi'
              )}
              data={exposureItemOptionsGrouped}
              value={(field.value ?? []).map(String)}
              onChange={val =>
                field.onChange(
                  (val ?? [])
                    .map(n => parseInt(String(n), 10))
                    .filter(n => !Number.isNaN(n))
                )
              }
              searchable
              error={fieldState.error?.message}
            />
          )}
        />
      )}
      {multiItem && (
        <Controller
          control={control}
          name='exposure.itemIds'
          render={({ field, fieldState }) => {
            const flat = (field.value ?? []) as number[];
            const emptySlot = 0;
            const groups =
              flat.length > 0
                ? chunk(flat, GROUP_SIZE)
                : [[emptySlot, emptySlot, emptySlot, emptySlot]];
            const displayGroups = (
              groups.length > 0
                ? groups
                : [[emptySlot, emptySlot, emptySlot, emptySlot]]
            ).map(g =>
              [...g, emptySlot, emptySlot, emptySlot].slice(0, GROUP_SIZE)
            );

            const setSlot = (
              groupIndex: number,
              slotIndex: number,
              value: number
            ) => {
              const newFlat = displayGroups.flat();
              const idx = groupIndex * GROUP_SIZE + slotIndex;
              if (idx < newFlat.length) {
                newFlat[idx] = value;
              } else {
                newFlat.push(value);
              }
              field.onChange(newFlat);
            };

            const addGroup = () => {
              field.onChange([
                ...flat,
                emptySlot,
                emptySlot,
                emptySlot,
                emptySlot,
              ]);
            };

            const removeGroup = (groupIndex: number) => {
              const newFlat = displayGroups.flat();
              const start = groupIndex * GROUP_SIZE;
              const next = newFlat
                .slice(0, start)
                .concat(newFlat.slice(start + GROUP_SIZE));
              field.onChange(next);
            };

            return (
              <Stack gap='md'>
                <Text size='sm' fw={500}>
                  {t('fe_foods', 'Lista elementi')}
                </Text>
                <Text size='xs' c='dimmed'>
                  {t(
                    'fe_multi_item_groups_description',
                    'Ogni gruppo contiene fino a 4 elementi mostrati insieme; lo stesso item può essere ripetuto.'
                  )}
                </Text>
                {fieldState.error?.message && (
                  <Text size='sm' c='red'>
                    {fieldState.error.message}
                  </Text>
                )}
                {displayGroups.map((group, groupIndex) => (
                  <Paper key={groupIndex} withBorder p='md' radius='sm'>
                    <Group justify='space-between' mb='xs'>
                      <Text size='sm' fw={500}>
                        {t('fe_multi_item_group_label', 'Gruppo {{n}}', {
                          n: groupIndex + 1,
                        })}
                      </Text>
                      {displayGroups.length > 1 && (
                        <ActionIcon
                          variant='subtle'
                          color='red'
                          size='sm'
                          onClick={() => removeGroup(groupIndex)}
                          aria-label={t(
                            'fe_multi_item_remove_group',
                            'Rimuovi gruppo'
                          )}
                        >
                          <Trash2 size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                    <Group gap='xs'>
                      {[0, 1, 2, 3].map(slotIndex => (
                        <Select
                          key={slotIndex}
                          placeholder={t(
                            'fe_multi_item_slot_placeholder',
                            'Elemento {{n}}',
                            {
                              n: slotIndex + 1,
                            }
                          )}
                          data={multiItemSlotOptions}
                          value={
                            group[slotIndex] === emptySlot
                              ? ''
                              : String(group[slotIndex])
                          }
                          onChange={v =>
                            setSlot(
                              groupIndex,
                              slotIndex,
                              v === '' || v == null
                                ? emptySlot
                                : parseInt(String(v), 10)
                            )
                          }
                          searchable
                          clearable
                          style={{ flex: 1 }}
                        />
                      ))}
                    </Group>
                  </Paper>
                ))}
                <Button
                  variant='light'
                  leftSection={<Plus size={16} />}
                  onClick={addGroup}
                >
                  {t(
                    'fe_multi_item_add_group',
                    'Aggiungi gruppo di 4 elementi'
                  )}
                </Button>
              </Stack>
            );
          }}
        />
      )}
    </Stack>
  );
}
